/**
 * ClientFilterToolbar Component (Story 3.7)
 *
 * Toolbar with search input, multi-column filter dropdown (tags, industry, platform),
 * view switcher, active filter chips, and result count for the client list.
 */

import { useState } from "react";
import {
  Input,
  Button,
  cn,
} from "@provenleap/ui";
import { MagnifyingGlass, X, Funnel, Check, Archive, CaretDown, Users } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "motion/react";
import { TagPill } from "./TagPill";
import { getIndustryIcon } from "./IndustryPicker";
import { ViewSwitcher, type ClientViewMode } from "./ViewSwitcher";
import { PICKER_PLATFORMS, logoUrl } from "../../lib/integrations";
import { ColumnVisibilityMenu } from "../shared/columnVisibility";
import { CLIENT_HIDEABLE_COLUMNS } from "./clientColumns";
import type { TenantTag } from "./types";

type FilterTab = "tags" | "industry" | "platform";

interface ClientFilterToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  selectedTagIds: string[];
  allTags: TenantTag[];
  tagsLoading: boolean;
  onToggleTag: (tagId: string) => void;
  selectedIndustries: string[];
  allIndustries: string[];
  onToggleIndustry: (industry: string) => void;
  selectedPlatforms: string[];
  allPlatforms: string[];
  onTogglePlatform: (platform: string) => void;
  onClearFilters: () => void;
  resultCount: number;
  totalCount: number;
  hasActiveFilters: boolean;
  viewMode: ClientViewMode;
  onViewModeChange: (mode: ClientViewMode) => void;
  showArchived?: boolean;
  archivedCount?: number;
  archivedLoading?: boolean;
  canManageClients?: boolean;
  onToggleArchived?: () => void;
  onAddClient?: () => void;
  /** Column-visibility (table view only). Hidden column ids + toggle handler. */
  hiddenColumns?: string[];
  onToggleColumn?: (id: string) => void;
  /** Show the "Columns" control — only relevant in the table view. */
  showColumnsControl?: boolean;
}

export function ClientFilterToolbar({
  search,
  onSearchChange,
  selectedTagIds,
  allTags,
  tagsLoading,
  onToggleTag,
  selectedIndustries,
  allIndustries,
  onToggleIndustry,
  selectedPlatforms,
  allPlatforms,
  onTogglePlatform,
  onClearFilters,
  resultCount: _resultCount,
  totalCount: _totalCount,
  hasActiveFilters: _hasActiveFilters,
  viewMode,
  onViewModeChange,
  showArchived,
  archivedLoading = false,
  canManageClients = false,
  onToggleArchived,
  onAddClient,
  hiddenColumns = [],
  onToggleColumn,
  showColumnsControl = false,
}: ClientFilterToolbarProps) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<FilterTab>("industry");
  const [filterSearch, setFilterSearch] = useState("");

  const selectedTags = allTags.filter((t) => selectedTagIds.includes(t.id));
  const totalFilterCount = selectedTagIds.length + selectedIndustries.length + selectedPlatforms.length;

  const tabs: { key: FilterTab; label: string; count: number }[] = [
    { key: "industry", label: "Industry", count: selectedIndustries.length },
    { key: "platform", label: "Integrations", count: selectedPlatforms.length },
    { key: "tags", label: "Tags", count: selectedTagIds.length },
  ];

  return (
    <div className="mb-4 space-y-2">
      {/* Row 1: Search + Filter + View + Archived */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search input */}
        <div className="relative max-w-xs flex-1">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground"
          />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search clients..."
            aria-label="Search clients"
            className="pl-9 pr-9 rounded-2xl text-foreground placeholder:text-foreground shadow-xs text-[13.5px]"
            style={{ border: "0.5px solid var(--hairline)" }}
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 min-h-11 min-w-11 flex items-center justify-center text-foreground/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Columns dropdown — table view only (show/hide table columns) */}
        {showColumnsControl && onToggleColumn && (
          <ColumnVisibilityMenu
            columns={CLIENT_HIDEABLE_COLUMNS}
            hiddenColumns={hiddenColumns}
            onToggleColumn={onToggleColumn}
          />
        )}

        {/* Filters dropdown */}
        <div className="relative">
          <Button
            variant="outline"
            disabled={tagsLoading}
            className="shrink-0 rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
            style={{ border: "0.5px solid var(--hairline)" }}
            onClick={() => { setFilterOpen(!filterOpen); setFilterSearch(""); }}
          >
            <Funnel size={16} className="mr-1" />
            Filters
            {totalFilterCount > 0 && (
              <span className="ml-1 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {totalFilterCount}
              </span>
            )}
            <CaretDown size={12} className="ml-1" />
          </Button>

          <AnimatePresence>
          {filterOpen && (
            <>
              {/* Backdrop */}
              <div className="fixed inset-0 z-40" onClick={() => { setFilterOpen(false); setFilterSearch(""); }} />

              {/* Dropdown panel */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -4 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                className="absolute left-0 top-full mt-2.5 z-50 w-72 bg-popover rounded-2xl shadow-md p-2"
                style={{ border: "0.5px solid var(--hairline)" }}
              >
                {/* Arrow pointing up */}
                <div
                  className="absolute -top-[5px] left-6 w-2.5 h-2.5 bg-popover rotate-45 z-10"
                  style={{ borderLeft: "0.5px solid var(--hairline)", borderTop: "0.5px solid var(--hairline)" }}
                />
                {/* Tab bar */}
                <div className="flex mx-4 pb-0">
                  {tabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => { setActiveTab(tab.key); setFilterSearch(""); }}
                      className={cn(
                        "flex-1 px-3 py-2.5 text-[13px] font-medium transition-colors cursor-pointer",
                        activeTab === tab.key
                          ? "text-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Search */}
                <div className="px-2 pt-2 pb-0.5">
                  <div className="relative">
                    <MagnifyingGlass size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      value={filterSearch}
                      onChange={(e) => setFilterSearch(e.target.value)}
                      placeholder={`Search ${activeTab === "tags" ? "tags" : activeTab === "industry" ? "industries" : "integrations"}...`}
                      className="w-full pl-8 pr-3 py-2 text-[13px] text-foreground bg-transparent placeholder:text-muted-foreground placeholder:font-normal font-medium rounded-lg border-[0.5px] border-input focus:outline-none focus-visible:outline-none"
                      style={{ outline: "none", boxShadow: "none" }}
                    />
                  </div>
                </div>

                {/* Tab content */}
                <div className="max-h-[260px] overflow-y-auto pt-2 pb-1">
                  {/* Tags tab */}
                  {activeTab === "tags" && (
                    <>
                      {allTags.length === 0 ? (
                        <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                          No tags created yet
                        </div>
                      ) : (
                        <>
                          {[...allTags]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .filter((tag) => !filterSearch || tag.name.toLowerCase().includes(filterSearch.toLowerCase()))
                            .map((tag) => {
                              const isSelected = selectedTagIds.includes(tag.id);
                              return (
                                <button
                                  key={tag.id}
                                  type="button"
                                  onClick={() => onToggleTag(tag.id)}
                                  className="flex items-center w-[calc(100%-12px)] ml-1 px-2 py-2 text-[13px] rounded-lg border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                                >
                                  <span
                                    className={cn(
                                      "w-4 h-4 border rounded mr-2 flex items-center justify-center shrink-0",
                                      isSelected && "bg-primary border-primary text-white"
                                    )}
                                  >
                                    {isSelected && <Check size={12} />}
                                  </span>
                                  <span
                                    className="w-3 h-3 rounded-full mr-2 shrink-0"
                                    style={{ backgroundColor: tag.color }}
                                  />
                                  {tag.name}
                                </button>
                              );
                            })}
                        </>
                      )}
                    </>
                  )}

                  {/* Industry tab */}
                  {activeTab === "industry" && (
                    <>
                      {allIndustries.length === 0 ? (
                        <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                          No industries yet
                        </div>
                      ) : (
                        allIndustries.filter((ind) => !filterSearch || ind.toLowerCase().includes(filterSearch.toLowerCase())).map((industry) => {
                          const isSelected = selectedIndustries.includes(industry);
                          const IndustryIcon = getIndustryIcon(industry);
                          return (
                            <button
                              key={industry}
                              type="button"
                              onClick={() => onToggleIndustry(industry)}
                              className="flex items-center w-[calc(100%-12px)] ml-1 px-2 py-2 text-[13px] rounded-lg border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                            >
                              <span
                                className={cn(
                                  "w-4 h-4 border rounded mr-2 flex items-center justify-center shrink-0",
                                  isSelected && "bg-primary border-primary text-white"
                                )}
                              >
                                {isSelected && <Check size={12} />}
                              </span>
                              {IndustryIcon && <IndustryIcon size={16} className="shrink-0 text-foreground mr-1.5" />}
                              {industry}
                            </button>
                          );
                        })
                      )}
                    </>
                  )}

                  {/* Platform tab */}
                  {activeTab === "platform" && (
                    <>
                      {allPlatforms.length === 0 ? (
                        <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                          No platforms yet
                        </div>
                      ) : (
                        allPlatforms.filter((pid) => {
                          if (!filterSearch) return true;
                          const meta = PICKER_PLATFORMS.find((p) => p.platform === pid);
                          return (meta?.displayName ?? pid).toLowerCase().includes(filterSearch.toLowerCase());
                        }).map((platformId) => {
                          const isSelected = selectedPlatforms.includes(platformId);
                          const platformMeta = PICKER_PLATFORMS.find((p) => p.platform === platformId);
                          return (
                            <button
                              key={platformId}
                              type="button"
                              onClick={() => onTogglePlatform(platformId)}
                              className="flex items-center w-[calc(100%-12px)] ml-1 px-2 py-2 text-[13px] rounded-lg border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                            >
                              <span
                                className={cn(
                                  "w-4 h-4 border rounded mr-2 flex items-center justify-center shrink-0",
                                  isSelected && "bg-primary border-primary text-white"
                                )}
                              >
                                {isSelected && <Check size={12} />}
                              </span>
                              {platformMeta?.domain && (
                                <img
                                  src={logoUrl(platformMeta.domain)}
                                  alt=""
                                  className="w-4 h-4 rounded-full mr-2 shrink-0 object-contain"
                                  loading="lazy"
                                />
                              )}
                              {platformMeta?.displayName ?? platformId}
                            </button>
                          );
                        })
                      )}
                    </>
                  )}
                </div>

              </motion.div>
            </>
          )}
          </AnimatePresence>
        </div>

        {/* View switcher + Show Archived — pushed to the right */}
        <div className="flex gap-4 sm:ml-auto">
        {/* View switcher */}
        <ViewSwitcher view={viewMode} onViewChange={onViewModeChange} />

        {canManageClients && onToggleArchived && (
          <Button
            variant="outline"
            onClick={onToggleArchived}
            disabled={archivedLoading && showArchived}
            className="shrink-0 rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
            style={{ border: "0.5px solid var(--hairline)" }}
          >
            <Archive size={16} className="mr-1" />
            {showArchived ? "Hide Archived" : "Show Archived"}
          </Button>
        )}

        {canManageClients && onAddClient && (
          <Button variant="brand" className="shrink-0 rounded-2xl text-[13px] cursor-pointer" onClick={onAddClient}>
            <Users size={16} className="mr-1" />
            Create Client
          </Button>
        )}
        </div>
      </div>

      {/* Row 2: Active filter chips + result count */}
      {totalFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {/* Tag chips */}
          {tagsLoading
            ? selectedTagIds.map((id) => (
                <span key={id} className="h-5 w-16 rounded-full bg-muted animate-pulse" />
              ))
            : selectedTags.map((tag) => (
                <TagPill
                  key={tag.id}
                  tag={tag}
                  size="sm"
                  className="!text-[13px]"
                  onRemove={() => onToggleTag(tag.id)}
                />
              ))}
          {/* Industry chips */}
          {selectedIndustries.map((industry) => {
            const IndustryIcon = getIndustryIcon(industry);
            return (
            <span
              key={`industry-${industry}`}
              className="inline-flex items-center w-fit rounded-2xl bg-background border-[0.5px] border-input px-4 text-[13px] h-10 font-medium text-foreground whitespace-nowrap"
            >
              {IndustryIcon && <IndustryIcon size={14} className="shrink-0 text-foreground mr-1.5" />}
              {industry}
              <button
                type="button"
                onClick={() => onToggleIndustry(industry)}
                className="ml-2.5 text-foreground hover:text-destructive transition-colors leading-none flex items-center shrink-0 cursor-pointer"
                style={{ minWidth: 0, minHeight: 0 }}
              >
                <X size={14} />
              </button>
            </span>
            );
          })}
          {/* Platform chips */}
          {selectedPlatforms.map((platformId) => {
            const meta = PICKER_PLATFORMS.find((p) => p.platform === platformId);
            return (
              <span
                key={`platform-${platformId}`}
                className="inline-flex items-center w-fit rounded-2xl bg-background border-[0.5px] border-input px-4 text-[13px] h-10 font-medium text-foreground whitespace-nowrap"
              >
                {meta?.domain && (
                  <img src={logoUrl(meta.domain)} alt="" className="w-3.5 h-3.5 rounded-full mr-1.5 object-contain" loading="lazy" />
                )}
                {meta?.displayName ?? platformId}
                <button
                  type="button"
                  onClick={() => onTogglePlatform(platformId)}
                  className="ml-2.5 text-foreground hover:text-destructive transition-colors leading-none flex items-center shrink-0 cursor-pointer"
                  style={{ minWidth: 0, minHeight: 0 }}
                >
                  <X size={14} />
                </button>
              </span>
            );
          })}
          {!tagsLoading && (
            <button
              type="button"
              onClick={onClearFilters}
              className="text-[13px] hover:text-foreground min-h-[44px] px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
              style={{ color: "#5A7396" }}
            >
              Clear all
            </button>
          )}
        </div>
      )}

    </div>
  );
}
